import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { ArrowLeft, Star, StarOff, Users, BookOpen, Filter, RefreshCw, Activity, Shield, Trash2, Upload, Image as ImageIcon, CheckCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { getLevel } from '@/utils/readingLevels';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, token, nightMode } = useContext(AuthContext);
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [debugMode, setDebugMode] = useState(false);
  const [imageProvider, setImageProvider] = useState('google');
  const [featuringStory, setFeaturingStory] = useState(null);
  const [storyFilter, setStoryFilter] = useState('all'); // 'all', 'featured', 'not-featured'
  const [skip, setSkip] = useState(0);
  const [total, setTotal] = useState(0);
  const limit = 20;

  const [activeTab, setActiveTab] = useState('stories'); // 'stories', 'users', or 'images'
  const [users, setUsers] = useState([]);
  const [userSkip, setUserSkip] = useState(0);
  const [userTotal, setUserTotal] = useState(0);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [takeoverStories, setTakeoverStories] = useState(false);
  const [takeoverChildren, setTakeoverChildren] = useState(false);
  const [takeoverAvatars, setTakeoverAvatars] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);

  // Image Updater state
  const [targetType, setTargetType] = useState('child');
  const [action, setAction] = useState('set');
  const [targetId, setTargetId] = useState('');
  const [file, setFile] = useState(null);
  const [b64String, setB64String] = useState('');
  const [updatingImage, setUpdatingImage] = useState(false);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      fetchEligibleStories();
      fetchDebugMode();
      fetchImageProvider();
      if (activeTab === 'users') {
        fetchUsers();
      }
    }
  }, [isAdmin, activeTab]);

  const fetchEligibleStories = async (loadMore = false) => {
    try {
      setLoading(true);
      const currentSkip = loadMore ? skip : 0;
      
      const response = await axios.get(
        `${API}/admin/stories/eligible-for-featuring?skip=${currentSkip}&limit=${limit}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newStories = response.data.stories;
      
      if (loadMore) {
        setStories(prev => [...prev, ...newStories]);
      } else {
        setStories(newStories);
      }
      
      setTotal(response.data.total);
      setSkip(currentSkip + newStories.length);
    } catch (error) {
      console.error('Failed to fetch stories:', error);
      toast.error('Failed to load stories');
    } finally {
      setLoading(false);
    }
  };

  const fetchDebugMode = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/debug-mode`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDebugMode(response.data.debug_logging);
    } catch (error) {
      console.error('Failed to fetch debug mode:', error);
    }
  };

  const handleToggleDebug = async () => {
    try {
      const newMode = !debugMode;
      await axios.post(
        `${API}/admin/settings/debug-mode?enabled=${newMode}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDebugMode(newMode);
      toast.success(`Debug logging ${newMode ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to toggle debug mode:', error);
      toast.error('Failed to update debug mode');
    }
  };

  const fetchImageProvider = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/image-provider`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImageProvider(response.data.image_provider);
    } catch (error) {
      console.error('Failed to fetch image provider:', error);
    }
  };

  const handleUpdateImageProvider = async (provider) => {
    try {
      await axios.post(
        `${API}/admin/settings/image-provider?provider=${provider}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setImageProvider(provider);
      toast.success(`Image provider set to ${provider === 'google' ? 'Google' : 'Cloudflare/Flux'}`);
    } catch (error) {
      console.error('Failed to update image provider:', error);
      toast.error('Failed to update image provider');
    }
  };

  const handleFeatureToggle = async (storyId, currentlyFeatured) => {
    setFeaturingStory(storyId);
    
    try {
      await axios.post(
        `${API}/admin/stories/${storyId}/feature?feature=${!currentlyFeatured}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setStories(prev => prev.map(s => 
        s.id === storyId ? { ...s, admin_featured: !currentlyFeatured } : s
      ));
      
      toast.success(currentlyFeatured ? 'Story unfeatured' : 'Story featured in community!');
    } catch (error) {
      console.error('Failed to toggle feature:', error);
      toast.error(error.response?.data?.detail || 'Failed to update story');
    } finally {
      setFeaturingStory(null);
    }
  };

  const fetchUsers = async (loadMore = false) => {
    try {
      setLoadingUsers(true);
      const currentSkip = loadMore ? userSkip : 0;
      
      const response = await axios.get(
        `${API}/admin/users?skip=${currentSkip}&limit=${limit}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newUsers = response.data.users;
      
      if (loadMore) {
        setUsers(prev => [...prev, ...newUsers]);
      } else {
        setUsers(newUsers);
      }
      
      setUserTotal(response.data.total);
      setUserSkip(currentSkip + newUsers.length);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    setDeletingUser(true);
    try {
      await axios.delete(
        `${API}/admin/users/${userToDelete.id}?takeover_stories=${takeoverStories}&takeover_children=${takeoverChildren}&takeover_avatars=${takeoverAvatars}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${userToDelete.email} deleted successfully.`);
      setShowDeleteDialog(false);
      setUserToDelete(null);
      fetchUsers(); // Refresh the list
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user.');
    } finally {
      setDeletingUser(false);
    }
  };

  const handleTakeover = async (storyId) => {
    if (!window.confirm('Are you sure you want to take ownership of this story? It will be moved to your library.')) return;

    try {
      await axios.post(
        `${API}/admin/stories/${storyId}/takeover`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Story ownership transferred to you');
      fetchEligibleStories();
    } catch (error) {
      console.error('Failed to takeover story:', error);
      toast.error(error.response?.data?.detail || 'Failed to takeover story');
    }
  };

  const handleImageUpdate = async (e) => {
    e.preventDefault();
    if (!targetId) return toast.error("Please provide a Target ID");
    if (action !== 'delete' && !file && !b64String) return toast.error("Please provide an image file or Base64 string");

    setUpdatingImage(true);
    const formData = new FormData();
    formData.append('target_type', targetType);
    formData.append('target_id', targetId);
    formData.append('action', action);
    
    if (action !== 'delete') {
      if (file) {
        formData.append('image_file', file);
      } else {
        formData.append('image_b64', b64String);
      }
    }

    try {
      await axios.post(`${API}/admin/update-image`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success(`${targetType} image ${action === 'delete' ? 'deleted' : 'updated'} successfully!`);
      setTargetId('');
      setFile(null);
      setB64String('');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to update image");
    } finally {
      setUpdatingImage(false);
    }
  };

  // Filter stories based on selection
  const filteredStories = stories.filter(story => {
    if (storyFilter === 'featured') return story.admin_featured;
    if (storyFilter === 'not-featured') return !story.admin_featured;
    return true;
  });

  if (!isAdmin) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${nightMode ? 'night-gradient' : 'playful-gradient'}`}>
        <div className="story-card text-center py-12 px-8 max-w-md">
          <Users className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary)' }} />
          <h1 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            Access Denied
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            You need admin privileges to access this page.
          </p>
          <Button
            onClick={() => navigate('/dashboard')}
            className="mt-6 pill-button"
            style={{ background: 'var(--accent-2)', color: 'white' }}
          >
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${nightMode ? 'night-gradient' : 'playful-gradient'} page-enter`}>
      {/* Header */}
      <header className="p-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/dashboard')}
            className="rounded-full"
          >
            <ArrowLeft className="w-6 h-6" style={{ color: 'var(--text-primary)' }} />
          </Button>
          <div className="flex items-center gap-3">
            <Star className="w-10 h-10" style={{ color: 'var(--accent-4)' }} strokeWidth={2.5} />
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
              Admin Panel - Feature Stories
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 mr-2">
            <Label className="text-xs font-sketch hidden sm:inline" style={{ color: 'var(--text-secondary)' }}>AI Engine:</Label>
            <select
              value={imageProvider}
              onChange={(e) => handleUpdateImageProvider(e.target.value)}
              className="px-3 py-1.5 rounded-full text-xs border font-sketch"
              style={{
                background: nightMode ? 'var(--bg-secondary)' : 'white',
                borderColor: nightMode ? 'var(--sketch-border)' : '#e5e7eb',
                color: 'var(--text-primary)'
              }}
            >
              <option value="google">Google Gemini</option>
              <option value="cloudflare_flux">Cloudflare/Flux</option>
            </select>
          </div>
          <Button
            onClick={handleToggleDebug}
            variant={debugMode ? "default" : "outline"}
            className={`rounded-full ${debugMode ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
          >
            <Activity className="w-4 h-4 mr-2" />
            Debug is {debugMode ? 'ON' : 'OFF'}
          </Button>
          <Button
            onClick={() => fetchEligibleStories(false)}
            variant="outline"
            className="rounded-full"
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      {/* Stats & Filters */}
      <div className="container mx-auto px-6 mb-6">
        <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
          <button
            onClick={() => setActiveTab('stories')}
            className={`px-4 py-2 text-sm font-medium ${activeTab === 'stories' ? 'border-b-2 border-purple-500 text-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Feature Stories
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 text-sm font-medium ${activeTab === 'users' ? 'border-b-2 border-purple-500 text-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            User Management
          </button>
          <button
            onClick={() => setActiveTab('images')}
            className={`px-4 py-2 text-sm font-medium ${activeTab === 'images' ? 'border-b-2 border-purple-500 text-purple-600' : 'text-gray-500 hover:text-gray-700'}`}
          >
            Image Overrider
          </button>
        </div>

        {activeTab === 'stories' && (
          <div className="story-card p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold" style={{ color: 'var(--accent-2)' }}>
                  {stories.filter(s => s.admin_featured).length}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Featured</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                  {total}
                </p>
                <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Eligible</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" style={{ color: 'var(--text-secondary)' }} />
              <select
                value={storyFilter}
                onChange={(e) => setStoryFilter(e.target.value)}
                className="px-3 py-1.5 rounded-lg text-sm border"
                style={{
                  background: nightMode ? 'var(--bg-secondary)' : 'white',
                  borderColor: nightMode ? 'var(--sketch-border)' : '#e5e7eb',
                  color: 'var(--text-primary)'
                }}
              >
                <option value="all">All Stories</option>
                <option value="featured">Featured Only</option>
                <option value="not-featured">Not Featured</option>
              </select>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="story-card p-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="text-center">
              <p className="text-2xl font-bold" style={{ color: 'var(--accent-2)' }}>
                {userTotal}
              </p>
              <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>Total Users</p>
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Stories Table */}
      <div className="container mx-auto px-6 pb-12">
        {activeTab === 'stories' && (
          <>
          {loading && stories.length === 0 ? (
          <div className="text-center py-12">
            <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" style={{ color: 'var(--accent-2)' }} />
            <p style={{ color: 'var(--text-secondary)' }}>Loading stories...</p>
          </div>
        ) : filteredStories.length === 0 ? (
          <div className="text-center py-12">
            <BookOpen className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary)' }} />
            <p style={{ color: 'var(--text-secondary)' }}>No stories found</p>
          </div>
        ) : (
            <div className="space-y-4">
            {filteredStories.map((story) => (
              <div
                key={story.id}
                className="story-card p-2 flex flex-col sm:flex-row items-start sm:items-center gap-4"
              >
                {/* Thumbnail */}
                <div className="w-full sm:w-24 h-20 sm:h-16 rounded-lg overflow-hidden flex-shrink-0">
                  {story.thumbnail ? (
                    <img
                      src={`data:image/png;base64,${story.thumbnail}`}
                      alt={story.title}
                      className="w-full h-full object-cover"
                    />
                  ) : story.images && story.images.length > 0 ? (
                    <img
                      src={`data:image/png;base64,${story.images[0]}`}
                      alt={story.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div 
                      className="w-full h-full flex items-center justify-center"
                      style={{ background: 'var(--accent-1)' }}
                    >
                      <BookOpen className="w-6 h-6" style={{ color: 'var(--accent-2)' }} />
                    </div>
                  )}
                </div>

                {/* Story Info */}
                <div className="flex-1 min-w-0">
                  <h3 
                    className="font-bold text-lg truncate"
                    style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}
                  >
                    {story.title}
                  </h3>
                  {story.summary && (
                    <p className="text-sm mt-1 line-clamp-2" style={{ color: 'var(--text-secondary)' }}>
                      {story.summary}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span 
                      className="text-xs px-2 py-0.5 rounded-full capitalize"
                      style={{ 
                        background: nightMode ? 'var(--accent-2)' : 'var(--accent-1)', 
                        color: nightMode ? 'white' : 'var(--accent-2)' 
                      }}
                    >
                      {getLevel(story.reading_level)}
                    </span>
                    <span 
                      className="text-xs px-2 py-0.5 rounded-full capitalize"
                      style={{ 
                        background: story.owner_tier === 'free' ? 'var(--accent-3)' : 'var(--accent-5)',
                        color: 'white'
                      }}
                    >
                      {story.owner_tier} tier
                    </span>
                    {story.community_shared && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500 text-white">
                        User Shared
                      </span>
                    )}
                    {story.admin_featured && (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-green-500 text-white">
                        Featured
                      </span>
                    )}
                  </div>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                    By: {story.owner_email}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <Button
                    onClick={() => navigate(`/story/${story.id}`)}
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-none"
                  >
                    View
                  </Button>
                  <Button
                    onClick={() => handleTakeover(story.id)}
                    variant="outline"
                    size="sm"
                    className="flex-1 sm:flex-none text-orange-600 border-orange-200 hover:bg-orange-50"
                    title="Take ownership"
                  >
                    <Shield className="w-4 h-4 mr-1" />
                    Takeover
                  </Button>
                  <Button
                    onClick={() => handleFeatureToggle(story.id, story.admin_featured)}
                    disabled={featuringStory === story.id}
                    size="sm"
                    className={`flex-1 sm:flex-none ${story.admin_featured ? '' : 'text-white'}`}
                    style={{
                      background: story.admin_featured ? 'transparent' : 'var(--accent-5)',
                      borderColor: story.admin_featured ? 'var(--accent-5)' : 'transparent',
                      color: story.admin_featured ? 'var(--accent-5)' : 'white'
                    }}
                    variant={story.admin_featured ? 'outline' : 'default'}
                  >
                    {featuringStory === story.id ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : story.admin_featured ? (
                      <>
                        <StarOff className="w-4 h-4 mr-1" />
                        Unfeature
                      </>
                    ) : (
                      <>
                        <Star className="w-4 h-4 mr-1" />
                        Feature
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}

            {/* Load More */}
            {stories.length < total && (
              <div className="text-center mt-6">
                <Button
                  onClick={() => fetchEligibleStories(true)}
                  disabled={loading}
                  className="pill-button"
                  style={{ background: 'var(--accent-3)', color: 'white' }}
                >
                  {loading ? 'Loading...' : `Load More (${stories.length} of ${total})`}
                </Button>
              </div>
            )}
          </div>
        )}
          </>
        )}

        {activeTab === 'users' && (
          <>
            {loadingUsers && users.length === 0 ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 mx-auto mb-4 animate-spin" style={{ color: 'var(--accent-2)' }} />
                <p style={{ color: 'var(--text-secondary)' }}>Loading users...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--text-secondary)' }} />
                <p style={{ color: 'var(--text-secondary)' }}>No users found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {users.map((u) => (
                  <div key={u.id} className="story-card p-3 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-base" style={{ color: 'var(--text-primary)' }}>{u.display_name}</p>
                      <p className="text-sm text-gray-500">{u.email}</p>
                      <div className="flex flex-wrap items-center gap-2 mt-2 text-xs">
                        <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100 capitalize">
                          {u.subscription_tier || 'free'}
                        </span>
                        <span className="text-gray-400">
                          Created: {new Date(u.created_at).toLocaleDateString()}
                        </span>
                        {u.last_active_at && (
                          <span className="text-gray-400">
                            Last Active: {new Date(u.last_active_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 mt-2 text-xs" style={{ color: 'var(--text-secondary)' }}>
                        <span className="flex items-center gap-1"><Users className="w-3 h-3" /> Children: {u.child_count || 0}</span>
                        <span className="flex items-center gap-1"><Star className="w-3 h-3" /> Avatars: {u.avatar_count || 0}</span>
                        <span className="flex items-center gap-1"><BookOpen className="w-3 h-3" /> Stories: {u.story_count || 0}</span>
                      </div>
                      {u.last_session_created_at && (
                        <p className="text-[10px] text-gray-400 mt-1">
                          Last Login: {new Date(u.last_session_created_at).toLocaleString()}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setUserToDelete(u);
                          setShowDeleteDialog(true);
                          setTakeoverStories(false);
                          setTakeoverChildren(false);
                          setTakeoverAvatars(false);
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete User
                      </Button>
                    </div>
                  </div>
                ))}
                {users.length < userTotal && (
                  <div className="text-center mt-6">
                    <Button
                      onClick={() => fetchUsers(true)}
                      disabled={loadingUsers}
                      className="pill-button"
                      style={{ background: 'var(--accent-3)', color: 'white' }}
                    >
                      {loadingUsers ? 'Loading...' : `Load More (${users.length} of ${userTotal})`}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {activeTab === 'images' && (
          <div className="sketch-border p-6 rounded-2xl max-w-2xl mx-auto my-8" style={{ background: 'var(--bg-secondary)' }}>
            <div className="flex items-center gap-3 mb-6">
              <ImageIcon className="w-6 h-6 text-purple-500" />
              <h2 className="font-heading text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Manual Image Overrider</h2>
            </div>

            <form onSubmit={handleImageUpdate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-sketch uppercase opacity-70" style={{ color: 'var(--text-primary)' }}>Target Type</label>
                  <select 
                    value={targetType}
                    onChange={(e) => setTargetType(e.target.value)}
                    className="w-full p-2 rounded-lg bg-white dark:bg-gray-800 border-2 border-black/10 font-sketch"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <option value="child">Child Profile</option>
                    <option value="avatar">Avatar</option>
                    <option value="story">Story</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-sketch uppercase opacity-70" style={{ color: 'var(--text-primary)' }}>Entity UUID (ID)</label>
                  <input 
                    type="text" 
                    value={targetId}
                    onChange={(e) => setTargetId(e.target.value)}
                    placeholder="e.g. guest-child-charlotte"
                    className="w-full p-2 rounded-lg bg-white dark:bg-gray-800 border-2 border-black/10 font-sketch"
                    style={{ color: 'var(--text-primary)' }}
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-sketch uppercase opacity-70" style={{ color: 'var(--text-primary)' }}>Action</label>
                <select 
                  value={action}
                  onChange={(e) => setAction(e.target.value)}
                  className="w-full p-2 rounded-lg bg-white dark:bg-gray-800 border-2 border-black/10 font-sketch"
                  style={{ color: 'var(--text-primary)' }}
                >
                  <option value="set">Overwrite Existing (Set)</option>
                  {targetType === 'story' && <option value="add">Add New (Append to Array)</option>}
                  <option value="delete" className="text-red-500">Delete / Clear Images</option>
                </select>
              </div>

              {action !== 'delete' && (
                <>
                  <div className="space-y-1">
                    <label className="text-xs font-sketch uppercase opacity-70" style={{ color: 'var(--text-primary)' }}>Upload Image File</label>
                    <div className="relative">
                      <input 
                        type="file" 
                        onChange={(e) => { setFile(e.target.files[0]); setB64String(''); }}
                        className="w-full p-2 rounded-lg bg-white dark:bg-gray-800 border-2 border-dashed border-black/20 font-sketch file:mr-4 file:py-1 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100"
                        accept="image/*"
                        style={{ color: 'var(--text-primary)' }}
                      />
                    </div>
                  </div>

                  <div className="text-center py-2 opacity-50 font-sketch" style={{ color: 'var(--text-primary)' }}>--- OR ---</div>

                  <div className="space-y-1">
                    <label className="text-xs font-sketch uppercase opacity-70" style={{ color: 'var(--text-primary)' }}>Paste Base64 String</label>
                    <textarea 
                      value={b64String}
                      onChange={(e) => { setB64String(e.target.value); setFile(null); }}
                      className="w-full p-2 rounded-lg bg-white dark:bg-gray-800 border-2 border-black/10 font-mono text-xs h-24"
                      placeholder="data:image/png;base64,..."
                      style={{ color: 'var(--text-primary)' }}
                    />
                  </div>
                </>
              )}

              <Button 
                type="submit" 
                disabled={updatingImage} 
                variant={action === 'delete' ? 'destructive' : 'default'}
                className="w-full font-sketch h-12"
              >
                {updatingImage ? "Processing..." : action === 'delete' ? "Confirm Deletion" : "Apply Image Override"}
              </Button>
            </form>
          </div>
        )}
      </div>

      {/* Delete User Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the user <strong>{userToDelete?.email}</strong>? This action is irreversible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="takeover"
                checked={takeoverStories}
                onCheckedChange={setTakeoverStories}
              />
              <label
                htmlFor="takeover"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Take over and re-assign all stories from this user to your admin account.
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="takeover-children"
                checked={takeoverChildren}
                onCheckedChange={setTakeoverChildren}
              />
              <label
                htmlFor="takeover-children"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Take over and re-assign all children profiles.
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="takeover-avatars"
                checked={takeoverAvatars}
                onCheckedChange={setTakeoverAvatars}
              />
              <label
                htmlFor="takeover-avatars"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Take over and re-assign all avatars.
              </label>
            </div>
            <p className="text-xs text-gray-500">
              If unchecked, the respective data belonging to this user will be permanently deleted.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} disabled={deletingUser}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteUser} disabled={deletingUser}>
              {deletingUser ? 'Deleting...' : 'Confirm Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
